import { Link, useNavigate } from "react-router-dom";
import {
    ShoppingCart,
    LogOut,
    LayoutDashboard,
    UserRound,
    Package,
    KeyRound,
    Menu,
    X,
    Sparkles,
    ShoppingBag,
    Bot,
    Bell,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { TID } from "@/constants/testIds";

export default function Header() {
    const { user, requireAuth, logout } = useAuth();
    const { totalQty } = useCart();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [pendingFollowUps, setPendingFollowUps] = useState(0);
    const [triageCount, setTriageCount] = useState(0);

    const isAdmin = user?.role === "admin";
    const isDoctor = user?.role === "doctor";
    const approvedDoctor = isDoctor && user?.doctor_status === "approved";

    useEffect(() => {
        if (!approvedDoctor) {
            setPendingFollowUps(0);
            return;
        }
        api
            .get("/doctor/follow-ups")
            .then((r) => {
                const today = new Date().toISOString().slice(0, 10);
                const count = (r.data || []).filter(
                    (f) =>
                        f.status !== "done" &&
                        f.status !== "cancelled" &&
                        f.scheduled_date <= today,
                ).length;
                setPendingFollowUps(count);
            })
            .catch(() => setPendingFollowUps(0));
    }, [approvedDoctor, user?.user_id]);

    useEffect(() => {
        if (!approvedDoctor && !isAdmin) {
            setTriageCount(0);
            return;
        }
        let cancelled = false;
        async function tick() {
            try {
                const { data } = await api.get("/doctor/triage/unread-count");
                if (!cancelled) setTriageCount(data?.count || 0);
            } catch {
                if (!cancelled) setTriageCount(0);
            }
        }
        tick();
        // Refresh every 30 seconds so a Vaidya sees new red flags come in
        const t = setInterval(tick, 30000);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, [approvedDoctor, isAdmin, user?.user_id]);

    return (
        <header className="sticky top-0 z-30 border-b border-veda-border bg-veda-surface/95 backdrop-blur">
            <div className="veda-container flex h-16 items-center justify-between">
                <Link
                    to="/"
                    data-testid={TID.headerLogo}
                    className="flex items-center gap-2"
                >
                    <span className="grid h-8 w-8 place-items-center bg-veda-brand text-white">
                        <span className="font-display text-lg font-semibold">
                            S
                        </span>
                    </span>
                    <span className="font-display text-xl font-semibold text-veda-onSurface">
                        Sushen Herbs
                    </span>
                </Link>

                <div className="flex items-center gap-2">
                    <Link
                        to="/wellness/quiz"
                        data-testid={TID.headerNavWellness}
                        className="hidden items-center gap-1 text-xs font-medium text-veda-onSurface hover:text-veda-brand md:inline-flex"
                    >
                        <Sparkles size={13} /> Wellness
                    </Link>
                    <Link
                        to="/wellness/guide"
                        data-testid={TID.headerNavGuide}
                        className="hidden items-center gap-1 text-xs font-medium text-veda-onSurface hover:text-veda-brand md:ml-3 md:inline-flex"
                    >
                        <Bot size={13} /> Guide
                    </Link>
                    <Link
                        to="/shop"
                        data-testid={TID.headerNavShop}
                        className="hidden items-center gap-1 text-xs font-medium text-veda-onSurface hover:text-veda-brand md:ml-3 md:inline-flex"
                    >
                        <ShoppingBag size={13} /> Shop
                    </Link>
                    <span className="hidden h-4 w-px bg-veda-border md:mx-3 md:inline" />
                    {user ? (
                        <>
                            {isAdmin && (
                                <>
                                    <Link
                                        to="/admin/overview"
                                        data-testid={TID.headerAdminOverviewLink}
                                        className="hidden items-center gap-1 border border-veda-border px-3 py-1.5 text-xs font-medium text-veda-onSurface hover:bg-veda-surface2 sm:inline-flex"
                                    >
                                        <LayoutDashboard size={14} />
                                        Admin
                                    </Link>
                                    {triageCount > 0 && (
                                        <Link
                                            to="/doctor/triage"
                                            data-testid={TID.headerTriageBadge}
                                            title={`${triageCount} unclaimed red-flag case${triageCount === 1 ? "" : "s"}`}
                                            className="relative hidden items-center gap-1 bg-veda-error px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white sm:inline-flex"
                                        >
                                            <Bell size={12} className="animate-pulse" />
                                            Triage · {triageCount}
                                        </Link>
                                    )}
                                </>
                            )}
                            {approvedDoctor && (
                                <>
                                    <Link
                                        to="/doctor/dashboard"
                                        data-testid={TID.headerDoctorDashboardLink}
                                        className="hidden items-center gap-1 border border-veda-border px-3 py-1.5 text-xs font-medium text-veda-onSurface hover:bg-veda-surface2 sm:inline-flex"
                                    >
                                        <LayoutDashboard size={14} />
                                        Doctor
                                    </Link>
                                    {pendingFollowUps > 0 && (
                                        <Link
                                            to="/doctor/dashboard"
                                            data-testid={TID.headerFollowUpBadge}
                                            title={`${pendingFollowUps} follow-up${pendingFollowUps === 1 ? "" : "s"} due today or earlier`}
                                            className="relative hidden items-center gap-1 bg-veda-brand px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white sm:inline-flex"
                                        >
                                            <Bell size={12} />
                                            {pendingFollowUps}
                                        </Link>
                                    )}
                                    {triageCount > 0 && (
                                        <Link
                                            to="/doctor/triage"
                                            data-testid={TID.headerTriageBadge}
                                            title={`${triageCount} unclaimed red-flag case${triageCount === 1 ? "" : "s"}`}
                                            className="relative hidden items-center gap-1 bg-veda-error px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white sm:inline-flex"
                                        >
                                            <Bell size={12} className="animate-pulse" />
                                            Triage · {triageCount}
                                        </Link>
                                    )}
                                </>
                            )}
                            {(user.role === "customer" || !isAdmin) && (
                                <Link
                                    to="/cart"
                                    data-testid={TID.headerCartLink}
                                    className="relative p-1.5 text-veda-onSurface"
                                    aria-label="Cart"
                                >
                                    <ShoppingCart size={19} strokeWidth={1.75} />
                                    {totalQty > 0 && (
                                        <span
                                            data-testid={TID.cartBadge}
                                            className="absolute -right-1 -top-1 min-w-[18px] rounded-pill bg-veda-brand px-1.5 text-center text-[10px] font-semibold leading-[18px] text-white"
                                        >
                                            {totalQty}
                                        </span>
                                    )}
                                </Link>
                            )}
                            <button
                                onClick={() => setOpen((o) => !o)}
                                className="p-1.5 text-veda-onSurface"
                                aria-label="Menu"
                            >
                                {open ? <X size={19} /> : <Menu size={19} />}
                            </button>
                        </>
                    ) : (
                        <>
                            <Link
                                to="/auth/register-doctor"
                                data-testid={TID.headerJoinDoctorLink}
                                className="hidden items-center gap-1 border border-veda-border px-3 py-1.5 text-xs font-medium text-veda-onSurface hover:bg-veda-surface2 sm:inline-flex"
                            >
                                Join as Vaidya
                            </Link>
                            <button
                                data-testid={TID.headerSigninBtn}
                                onClick={() => requireAuth(null)}
                                className="bg-veda-inverse px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-veda-onInverse hover:opacity-90"
                            >
                                Sign in
                            </button>
                        </>
                    )}
                </div>
            </div>

            {open && user && (
                <div className="border-t border-veda-border bg-veda-surface">
                    <div className="veda-container py-3">
                    <div className="flex flex-col divide-y divide-veda-divider">
                        <div className="flex items-center gap-3 py-2">
                            <span className="grid h-9 w-9 place-items-center rounded-pill bg-veda-surface2">
                                <UserRound size={16} />
                            </span>
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-veda-onSurface">
                                    {user.name}
                                </p>
                                <p className="text-xs text-veda-muted">
                                    {user.email} · {user.role}
                                </p>
                            </div>
                        </div>
                        {user.role === "customer" && (
                            <Link
                                to="/dashboard"
                                data-testid={TID.headerNavDashboard}
                                onClick={() => setOpen(false)}
                                className="flex items-center gap-2 py-2 text-sm text-veda-onSurface"
                            >
                                <LayoutDashboard size={15} /> My dashboard
                            </Link>
                        )}
                        {user.role === "customer" && (
                            <Link
                                to="/orders"
                                data-testid={TID.headerOrdersLink}
                                onClick={() => setOpen(false)}
                                className="flex items-center gap-2 py-2 text-sm text-veda-onSurface"
                            >
                                <Package size={15} /> My orders
                            </Link>
                        )}
                        <Link
                            to="/account/password"
                            data-testid={TID.headerChangePasswordLink}
                            onClick={() => setOpen(false)}
                            className="flex items-center gap-2 py-2 text-sm text-veda-onSurface"
                        >
                            <KeyRound size={15} /> Change password
                        </Link>
                        {isAdmin && (
                            <>
                                <Link
                                    to="/admin/overview"
                                    onClick={() => setOpen(false)}
                                    className="py-2 text-sm text-veda-onSurface"
                                >
                                    Admin overview
                                </Link>
                                <Link
                                    to="/admin/products"
                                    onClick={() => setOpen(false)}
                                    className="py-2 text-sm text-veda-onSurface"
                                >
                                    Products
                                </Link>
                                <Link
                                    to="/admin/doctors"
                                    onClick={() => setOpen(false)}
                                    className="py-2 text-sm text-veda-onSurface"
                                >
                                    Doctors
                                </Link>
                                <Link
                                    to="/admin/orders"
                                    onClick={() => setOpen(false)}
                                    className="py-2 text-sm text-veda-onSurface"
                                >
                                    Orders
                                </Link>
                            </>
                        )}
                        {approvedDoctor && (
                            <>
                                <Link
                                    to="/doctor/dashboard"
                                    onClick={() => setOpen(false)}
                                    className="py-2 text-sm text-veda-onSurface"
                                >
                                    Dashboard
                                </Link>
                                <Link
                                    to="/doctor/chats"
                                    onClick={() => setOpen(false)}
                                    className="py-2 text-sm text-veda-onSurface"
                                >
                                    Patient chats
                                </Link>
                            </>
                        )}
                        <button
                            data-testid={TID.headerLogout}
                            onClick={async () => {
                                setOpen(false);
                                await logout();
                                nav("/");
                            }}
                            className="flex items-center gap-2 py-2 text-left text-sm text-veda-error"
                        >
                            <LogOut size={15} /> Sign out
                        </button>
                    </div>
                    </div>
                </div>
            )}
        </header>
    );
}
